import { inject, Injectable } from '@angular/core';
import {
  CartItem,
  CouponConfigurationResponse,
  CouponDiscountType,
  CurrencyCode,
  EndOrderDetails,
  InstallmentOption,
  calculateInstallmentSurcharge,
} from '@pedix-workspace/utils';
import {
  ShippingCostResponse,
  hasDeliveryCost,
  hasOutOfDeliveryZone,
} from '@pedix-workspace/shared-delivery';
import { calculateCartItemsTotal } from '@pedix-workspace/orders';
import { CouponValidationService } from '@pedix-workspace/pedixapp-core-services';
import { EndOrderForm } from './end-order-form/end-order-form.service';

export type EndOrderTotals = Pick<
  EndOrderDetails,
  | 'totalAmount'
  | 'totalAmountWithDiscount'
  | 'totalProductDiscount'
  | 'couponDiscount'
  | 'deliveryCost'
  | 'outOfDeliveryZone'
  | 'paymentMethodExtraCharge'
  | 'paymentMethodDiscount'
  | 'installmentSurcharge'
  | 'foodBankDonationAmount'
  | 'finalAmount'
  | 'currencyCode'
> & {
  promotionDiscount?: number;
  couponDiscountType?: CouponDiscountType;
  installmentCount?: number;
  installmentSurchargePercent?: number;
};

export type EndOrderTotalsParams = {
  cartItems: CartItem[];
  couponConfigurationSnapshot?: CouponConfigurationResponse;
  shippingCostResponse?: ShippingCostResponse;
  endOrderForm?: EndOrderForm;
  foodBankDonation?: number;
  currencyCode: CurrencyCode;
  promotionDiscount: number;
  selectedInstallments?: number;
  installmentOptions?: InstallmentOption[];
};

@Injectable({ providedIn: 'root' })
export class EndOrderTotalsService {
  private couponValidationService = inject(CouponValidationService);

  getEndOrderTotals({
    cartItems,
    endOrderForm,
    couponConfigurationSnapshot,
    shippingCostResponse,
    foodBankDonation,
    currencyCode,
    promotionDiscount,
    selectedInstallments,
    installmentOptions,
  }: EndOrderTotalsParams): EndOrderTotals {
    const totalAmount = calculateCartItemsTotal(cartItems, {
      usePriceDiscount: false,
    });
    const totalAmountWithDiscount = calculateCartItemsTotal(cartItems, {
      usePriceDiscount: true,
    });
    const totalProductDiscount = totalAmount - totalAmountWithDiscount;

    const deliveryCost = hasDeliveryCost(shippingCostResponse)
      ? shippingCostResponse.deliveryCost
      : 0;

    const couponDiscount = couponConfigurationSnapshot
      ? this.couponValidationService.calculateCouponDiscount(
          cartItems,
          couponConfigurationSnapshot,
          deliveryCost,
        )
      : 0;

    const paymentMethodDiscount = this.calculatePaymentMethodDiscountAmount({
      discount: endOrderForm?.paymentMethod?.discount,
      totalAmountWithDiscount,
      couponDiscount,
    });

    const outOfDeliveryZone = hasOutOfDeliveryZone(shippingCostResponse);

    const baseAmount =
      totalAmountWithDiscount -
      (couponDiscount || 0) -
      (paymentMethodDiscount || 0) -
      (promotionDiscount || 0);

    const { installmentSurcharge, installmentSurchargePercent } = calculateInstallmentSurcharge(
      baseAmount,
      installmentOptions,
      selectedInstallments,
    );

    const hasActiveInstallmentSelection = !!installmentOptions?.length && selectedInstallments != null;

    const paymentMethodExtraCharge = hasActiveInstallmentSelection
      ? 0
      : this.calculatePaymentMethodExtraChargeAmount({
          extraCharge: endOrderForm?.paymentMethod?.extraCharge,
          totalAmountWithDiscount,
          couponDiscount,
          deliveryCost,
        });
    const foodBankDonationAmount = endOrderForm?.bankFoodDonationChecked ? foodBankDonation : 0;

    const finalAmount = this.calculateTotalToBePaid({
      totalAmount,
      totalProductDiscount,
      couponDiscount,
      paymentMethodDiscount,
      deliveryCost,
      paymentMethodExtraCharge,
      installmentSurcharge,
      foodBankDonationAmount,
      promotionDiscount,
    });

    return {
      totalAmount,
      totalAmountWithDiscount,
      totalProductDiscount,
      deliveryCost,
      couponDiscount,
      couponDiscountType: couponConfigurationSnapshot?.discountType,
      paymentMethodExtraCharge,
      paymentMethodDiscount,
      installmentSurcharge,
      installmentCount: selectedInstallments,
      installmentSurchargePercent,
      finalAmount,
      outOfDeliveryZone,
      currencyCode,
      foodBankDonationAmount,
      promotionDiscount,
    };
  }

  calculatePaymentMethodDiscountAmount(data: {
    totalAmountWithDiscount: number;
    couponDiscount?: number;
    discount?: number;
  }) {
    if (!data.discount) {
      return 0;
    }
    const subtotal = data.totalAmountWithDiscount - (data.couponDiscount || 0);

    return (subtotal * data.discount) / 100;
  }

  calculatePaymentMethodExtraChargeAmount(data: {
    totalAmountWithDiscount: number;
    couponDiscount?: number;
    deliveryCost?: number;
    extraCharge?: number;
  }) {
    if (!data.extraCharge) {
      return 0;
    }
    const subtotal =
      data.totalAmountWithDiscount - (data.couponDiscount || 0) + (data.deliveryCost || 0);

    return (subtotal * data.extraCharge) / 100;
  }

  calculateTotalToBePaid(
    data: Pick<
      EndOrderDetails,
      | 'totalAmount'
      | 'totalProductDiscount'
      | 'couponDiscount'
      | 'paymentMethodDiscount'
      | 'deliveryCost'
      | 'paymentMethodExtraCharge'
      | 'installmentSurcharge'
      | 'foodBankDonationAmount'
    > & { promotionDiscount?: number },
  ) {
    const subtotal =
      data.totalAmount -
      data.totalProductDiscount -
      (data.couponDiscount || 0) -
      (data.paymentMethodDiscount || 0) -
      (data.promotionDiscount || 0) +
      (data.deliveryCost || 0) +
      (data.paymentMethodExtraCharge || 0) +
      (data.installmentSurcharge || 0);

    return subtotal + (data.foodBankDonationAmount || 0);
  }
}
